/**
 * StaticMap Component
 * Static map display for viewing locations (no interaction)
 * Uses OpenStreetMap + Leaflet (free, no API key required)
 */

import React from 'react';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet marker icon issue in Vite/Webpack
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import iconRetina from 'leaflet/dist/images/marker-icon-2x.png';

let DefaultIcon = L.icon({
  iconUrl: icon,
  iconRetinaUrl: iconRetina,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

interface StaticMapProps {
  latitude: number;
  longitude: number;
  height?: string;
  zoom?: number;
  showLink?: boolean;
}

export const StaticMap: React.FC<StaticMapProps> = ({
  latitude,
  longitude,
  height = '300px',
  zoom = 15,
  showLink = true
}) => {
  const position: [number, number] = [latitude, longitude];
  const openStreetMapUrl = `https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}&zoom=15`;

  return (
    <div className="space-y-2">
      <div className="relative">
        <MapContainer
          center={position}
          zoom={zoom}
          style={{ height, width: '100%', borderRadius: '8px' }}
          scrollWheelZoom={false}
          dragging={false}
          zoomControl={false}
          doubleClickZoom={false}
          touchZoom={false}
          attributionControl={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Marker position={position} />
        </MapContainer>

        {showLink && (
          <div className="absolute bottom-2 right-2 z-[1000]">
            <a
              href={openStreetMapUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/95 hover:bg-white text-sm font-medium text-blue-600 hover:text-blue-700 rounded-md shadow-md hover:shadow-lg transition-all duration-200 backdrop-blur-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>เปิดใน OpenStreetMap</span>
            </a>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded">
        <span className="font-medium">พิกัด GPS:</span>
        <span className="font-mono text-gray-700">
          {latitude.toFixed(8)}, {longitude.toFixed(8)}
        </span>
      </div>
    </div>
  );
};

export default StaticMap;
